/**
 * 화면 부품이 사용 기록기(`src/agent/recorder.ts`)에 알리는 작은 신호.
 *
 * 2026-09-29. 표 안의 체크·칸 수정은 DOM 의 change 를 내지 않고(체크칸은 `div`) 내부 상태만
 * 바꾼다. 팝업의 검색·선택도 스토어 안에서 끝난다. 기록기는 문서 리스너만으로는 그것을
 * 못 본다 — 그래서 부품이 **확정된 변경**을 창 사건(`elman:cell`, `elman:lookup`)으로 알린다.
 *
 * 부품은 기록기를 모른다. 듣는 쪽이 없으면 사건은 그냥 사라진다. 여기서 던지면 화면이
 * 깨지므로 전부 try/catch 안이다.
 */

export type CellSignal = {
  /** 표 이름(`elGrid`). 없으면 빈 글자. */
  grid: string;
  /** 자료 기준 행 번호. */
  index: number;
  /** 바꾼 칸의 헤더 id. */
  column: string;
  /** 바꾸기 전 행과 바꾼 뒤 행(같은 창 안이라 객체 그대로 넘긴다). */
  before: unknown;
  after: unknown;
  /** 바꾼 키와 새 값. */
  changes: Record<string, unknown>;
};

export type LookupSignal = {
  step: 'open' | 'query' | 'pick' | 'cancel';
  /** 팝업 종류(`TB_XCLIENT` 같은 조회 함수 이름, 없으면 팝업 이름). */
  popup: string;
  /** 검색어. */
  query?: string;
  /** 고른 행. */
  item?: unknown;
  /** 결과가 하나라 사람 손 없이 골라졌다. */
  auto?: boolean;
};

/**
 * DOM 의 change 없이 값을 확정한 칸. 날짜 칸은 달력에서 고르거나 Enter 로 확정하면
 * 곧바로 화면 함수를 부른다 — 점검계획 상세의 점검일자는 그 자리에서 서버에 반영된다.
 */
export type ValueSignal = {
  /** 값이 들어간 입력 요소. 기록기가 이 자리로 표 칸·앵커·라벨을 찾는다. */
  element: Element | null;
  value: string;
  /** 확정한 방법. */
  via: 'calendar' | 'enter';
};

function emit(name: string, detail: unknown): void {
  try {
    if (typeof window === 'undefined' || typeof CustomEvent !== 'function') return;
    window.dispatchEvent(new CustomEvent(name, { detail }));
  } catch {
    // 기록 신호가 화면을 깨뜨리면 안 된다.
  }
}

export function signalCell(detail: CellSignal): void {
  emit('elman:cell', detail);
}

export function signalLookup(detail: LookupSignal): void {
  emit('elman:lookup', detail);
}

export function signalValue(detail: ValueSignal): void {
  emit('elman:value', detail);
}
